import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { AMHS_PALETTE } from '../types';

export type StockerCranePose = {
  /** Carriage X/Y/Z in stocker local space (mast travels with X). */
  carriage: [number, number, number];
  /** Fork tip Z (extends from aisle toward port or slot). */
  forkZ: number;
  /** Whether fork arm is fully extended (always drawn short when idle). */
  forkVisible: boolean;
  /** Active cyan highlight while transferring. */
  active: boolean;
};

export type StockerCraneProps = {
  width: number;
  height: number;
  depth: number;
  opacity?: number;
  /** World FOUP scale — cradle sized to hold it. */
  foupScale?: number;
  /** Imperative pose updated every frame by the transfer controller. */
  poseRef: React.MutableRefObject<StockerCranePose>;
};

/**
 * Internal stocker crane (= intrabay I/O):
 * floor rail → traveling mast → Y-carriage → Z-fork.
 * Mast and carriage stay mechanically connected (no floating plate).
 */
export const StockerCrane: React.FC<StockerCraneProps> = ({
  width,
  height,
  depth,
  opacity = 1,
  foupScale = 5.5,
  poseRef,
}) => {
  const travelerRef = useRef<THREE.Group>(null);
  const carriageRef = useRef<THREE.Group>(null);
  const armRef = useRef<THREE.Mesh>(null);
  const cradleRef = useRef<THREE.Group>(null);
  const accentRef = useRef<THREE.MeshStandardMaterial>(null);

  const parkX = 0;
  const aisleZ = 0;
  const mastW = Math.max(0.22, width * 0.028);
  const mastD = Math.max(0.28, depth * 0.07);
  const railLen = Math.max(width * 0.72, 2.4);
  const carriageW = Math.max(1.1, width * 0.14, foupScale * 0.42);
  const carriageH = Math.max(0.32, height * 0.045);
  const carriageD = Math.max(0.85, depth * 0.2, foupScale * 0.28);
  const forkW = Math.max(0.65, foupScale * 0.22);
  // Cradle sized for the same FOUP the OHT drops (constant scale).
  const cradleW = Math.max(0.9, foupScale * 0.4);
  const cradleD = Math.max(0.7, foupScale * 0.42);
  const guideR = Math.max(0.04, mastW * 0.28);

  const lastPoseKeyRef = useRef('');

  useFrame(() => {
    const pose = poseRef.current;
    const x = pose.carriage[0];
    const y = pose.carriage[1];
    const z = pose.carriage[2];
    const poseKey = `${x.toFixed(3)}|${y.toFixed(3)}|${z.toFixed(3)}|${pose.forkZ.toFixed(3)}|${pose.active ? 1 : 0}`;
    if (poseKey === lastPoseKeyRef.current && !pose.active) {
      return;
    }
    lastPoseKeyRef.current = poseKey;

    // Whole mast traveler moves in X (and stays on aisle Z).
    if (travelerRef.current) {
      travelerRef.current.position.set(x, 0, z);
    }
    // Carriage climbs the mast in Y (local to traveler).
    if (carriageRef.current) {
      carriageRef.current.position.set(0, y, 0);
    }

    // Telescoping arm + fixed-size FOUP cradle at the tip (never scale the cradle).
    const reach = pose.forkZ - z;
    const armLen = Math.max(0.35, Math.abs(reach) + carriageD * 0.15);
    const tipSign = reach >= 0 ? 1 : -1;
    const tipZ = tipSign * (armLen * 0.5 + cradleD * 0.35);

    if (armRef.current) {
      armRef.current.visible = true;
      armRef.current.position.set(0, carriageH * 0.12, tipSign * armLen * 0.5);
      armRef.current.scale.set(1, 1, armLen);
    }
    if (cradleRef.current) {
      cradleRef.current.visible = true;
      // When retracted, park cradle just ahead of the carriage deck.
      if (Math.abs(reach) < 0.08) {
        cradleRef.current.position.set(0, carriageH * 0.18, carriageD * 0.55);
      } else {
        cradleRef.current.position.set(0, carriageH * 0.18, tipZ);
      }
    }

    if (accentRef.current) {
      if (pose.active) {
        accentRef.current.color.set('#31C8C8');
        accentRef.current.emissive.set('#31C8C8');
        accentRef.current.emissiveIntensity = 1.5;
      } else {
        accentRef.current.color.set('#31C8C8');
        accentRef.current.emissive.set('#31C8C8');
        accentRef.current.emissiveIntensity = 0.85;
      }
    }
  });

  const mat = {
    metal: {
      color: AMHS_PALETTE.metal,
      metalness: 0.85,
      roughness: 0.22,
      transparent: opacity < 1,
      opacity,
    },
    dark: {
      color: AMHS_PALETTE.metalDark,
      metalness: 0.9,
      roughness: 0.18,
      transparent: opacity < 1,
      opacity,
    },
    steel: {
      color: AMHS_PALETTE.tray,
      metalness: 0.88,
      roughness: 0.2,
      transparent: opacity < 1,
      opacity,
    },
    placement: {
      color: '#EEF2F6',
      metalness: 0.12,
      roughness: 0.38,
      transparent: opacity < 1,
      opacity,
    },
    rod: {
      color: '#F8FAFC',
      metalness: 0.18,
      roughness: 0.32,
      transparent: opacity < 1,
      opacity,
    },
  };

  return (
    <group name="StockerCrane">
      {/* Floor rail the mast rides on (aisle) — white road + rod stripe */}
      <mesh position={[parkX, 0.06, aisleZ]} castShadow receiveShadow>
        <boxGeometry args={[railLen, 0.1, Math.max(0.35, mastD * 1.35)]} />
        <meshStandardMaterial {...mat.dark} />
      </mesh>
      <mesh position={[parkX, 0.12, aisleZ]}>
        <boxGeometry args={[railLen * 0.98, 0.04, mastD * 0.55]} />
        <meshStandardMaterial {...mat.placement} />
      </mesh>
      <mesh position={[parkX, 0.155, aisleZ]}>
        <boxGeometry args={[railLen * 0.92, 0.018, mastD * 0.35]} />
        <meshStandardMaterial {...mat.rod} />
      </mesh>
      {/* Rail end stops */}
      {([-1, 1] as const).map((s) => (
        <mesh key={`stop-${s}`} position={[parkX + s * railLen * 0.48, 0.2, aisleZ]}>
          <boxGeometry args={[0.12, 0.28, mastD * 1.2]} />
          <meshStandardMaterial {...mat.metal} />
        </mesh>
      ))}

      {/* Traveling mast + carriage (X follows pose) */}
      <group ref={travelerRef} position={[parkX, 0, aisleZ]}>
        {/* Mast base / bogie locked to floor rail */}
        <mesh position={[0, 0.22, 0]} castShadow>
          <boxGeometry args={[mastW * 2.2, 0.28, mastD * 1.6]} />
          <meshStandardMaterial {...mat.dark} />
        </mesh>
        {([-0.55, 0.55] as const).map((sx) =>
          ([-0.45, 0.45] as const).map((sz) => (
            <mesh
              key={`wheel-${sx}-${sz}`}
              position={[mastW * sx, 0.1, mastD * sz]}
              rotation={[0, 0, Math.PI / 2]}
            >
              <cylinderGeometry args={[0.07, 0.07, 0.08, 10]} />
              <meshStandardMaterial {...mat.steel} />
            </mesh>
          ))
        )}

        {/* Vertical mast column — white center rod */}
        <mesh position={[0, height * 0.48, 0]} castShadow>
          <boxGeometry args={[mastW, height * 0.9, mastD]} />
          <meshStandardMaterial {...mat.rod} />
        </mesh>
        {/* Guide rail stripe on mast */}
        <mesh position={[mastW * 0.52, height * 0.48, 0]}>
          <boxGeometry args={[mastW * 0.22, height * 0.88, mastD * 0.7]} />
          <meshStandardMaterial
            ref={accentRef}
            color="#31C8C8"
            emissive="#31C8C8"
            emissiveIntensity={0.85}
            metalness={0.4}
            roughness={0.3}
            transparent={opacity < 1}
            opacity={opacity}
          />
        </mesh>
        {/* Top cross-head */}
        <mesh position={[0, height * 0.94, 0]} castShadow>
          <boxGeometry args={[mastW * 2.4, 0.16, mastD * 1.5]} />
          <meshStandardMaterial {...mat.dark} />
        </mesh>

        {/* Y-carriage clamped onto the mast */}
        <group ref={carriageRef} position={[0, height * 0.55, 0]}>
          {/* Clamp sleeves around mast */}
          <mesh position={[-mastW * 0.75, 0, 0]} castShadow>
            <boxGeometry args={[mastW * 0.55, carriageH * 1.1, mastD * 1.35]} />
            <meshStandardMaterial {...mat.dark} />
          </mesh>
          <mesh position={[mastW * 0.75, 0, 0]} castShadow>
            <boxGeometry args={[mastW * 0.55, carriageH * 1.1, mastD * 1.35]} />
            <meshStandardMaterial {...mat.dark} />
          </mesh>
          {/* Guide rollers gripping the mast */}
          {([-1, 1] as const).map((side) =>
            ([-0.35, 0.35] as const).map((oy) => (
              <mesh
                key={`roller-${side}-${oy}`}
                position={[side * mastW * 0.55, carriageH * oy, 0]}
                rotation={[0, 0, Math.PI / 2]}
              >
                <cylinderGeometry args={[guideR, guideR, mastW * 0.35, 10]} />
                <meshStandardMaterial {...mat.steel} />
              </mesh>
            ))
          )}
          {/* Carriage deck */}
          <mesh position={[0, 0, 0]} castShadow>
            <boxGeometry args={[carriageW, carriageH, carriageD]} />
            <meshStandardMaterial {...mat.dark} />
          </mesh>
          <mesh position={[0, carriageH * 0.55, carriageD * 0.05]}>
            <boxGeometry args={[carriageW * 0.9, 0.05, carriageD * 0.85]} />
            <meshStandardMaterial {...mat.placement} />
          </mesh>
          {/* Hoist motor housing */}
          <mesh position={[carriageW * 0.28, carriageH * 0.9, -carriageD * 0.15]} castShadow>
            <boxGeometry args={[carriageW * 0.35, carriageH * 0.7, carriageD * 0.4]} />
            <meshStandardMaterial {...mat.metal} />
          </mesh>

          {/* Telescoping arm (scaled) + fixed FOUP cradle at tip */}
          <mesh ref={armRef} position={[0, carriageH * 0.12, carriageD * 0.4]} castShadow>
            <boxGeometry args={[forkW * 0.7, 0.1, 1]} />
            <meshStandardMaterial {...mat.steel} />
          </mesh>
          {/* Center boom stripe (reads as attached to mast / center rod) */}
          <mesh position={[0, carriageH * 0.22, carriageD * 0.55]}>
            <boxGeometry args={[forkW * 0.22, 0.06, carriageD * 0.9]} />
            <meshStandardMaterial
              color="#31C8C8"
              emissive="#31C8C8"
              emissiveIntensity={0.7}
              metalness={0.45}
              roughness={0.3}
            />
          </mesh>

          <group ref={cradleRef} position={[0, carriageH * 0.18, carriageD * 0.55]}>
            {/* Attachment collar — joins cradle to telescoping arm */}
            <mesh position={[0, 0.02, -cradleD * 0.42]} castShadow>
              <boxGeometry args={[cradleW * 0.55, 0.14, cradleD * 0.22]} />
              <meshStandardMaterial {...mat.dark} />
            </mesh>
            <mesh position={[0, 0.02, -cradleD * 0.42]}>
              <cylinderGeometry args={[0.08, 0.08, 0.16, 10]} />
              <meshStandardMaterial
                color="#31C8C8"
                emissive="#31C8C8"
                emissiveIntensity={0.65}
                metalness={0.4}
                roughness={0.3}
              />
            </mesh>
            {/* White FOUP placement tray */}
            <mesh position={[0, 0.04, 0]} castShadow receiveShadow>
              <boxGeometry args={[cradleW * 0.92, 0.04, cradleD * 0.88]} />
              <meshStandardMaterial {...mat.placement} />
            </mesh>
            {/* Outer rectangular frame holding the FOUP */}
            <mesh castShadow>
              <boxGeometry args={[cradleW, 0.06, cradleD]} />
              <meshStandardMaterial {...mat.dark} />
            </mesh>
            {/* Side rails */}
            <mesh position={[-cradleW * 0.42, 0.08, 0]}>
              <boxGeometry args={[0.05, 0.16, cradleD * 0.95]} />
              <meshStandardMaterial {...mat.dark} />
            </mesh>
            <mesh position={[cradleW * 0.42, 0.08, 0]}>
              <boxGeometry args={[0.05, 0.16, cradleD * 0.95]} />
              <meshStandardMaterial {...mat.dark} />
            </mesh>
            {/* Twin fork tines under FOUP */}
            <mesh position={[-cradleW * 0.22, 0.02, cradleD * 0.05]}>
              <boxGeometry args={[cradleW * 0.18, 0.04, cradleD * 0.9]} />
              <meshStandardMaterial color={AMHS_PALETTE.metalDark} metalness={0.85} roughness={0.2} />
            </mesh>
            <mesh position={[cradleW * 0.22, 0.02, cradleD * 0.05]}>
              <boxGeometry args={[cradleW * 0.18, 0.04, cradleD * 0.9]} />
              <meshStandardMaterial color={AMHS_PALETTE.metalDark} metalness={0.85} roughness={0.2} />
            </mesh>
            {/* Front lip */}
            <mesh position={[0, 0.06, cradleD * 0.48]}>
              <boxGeometry args={[cradleW * 0.92, 0.1, 0.04]} />
              <meshStandardMaterial {...mat.dark} />
            </mesh>
          </group>
        </group>
      </group>
    </group>
  );
};

export default StockerCrane;
